import React, { useEffect } from "react";

import { Paper, Grid, Typography, Toolbar, Box } from "@mui/material";
import { Button, ButtonGroup } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { TooltipItem } from "chart.js";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import DashboardService from "../../../../services/dashboard.service";
import { useAppState } from "../../../../context/StateContext";
import { Chart, ChartType } from "../../../Generic/Chart";
import { brandSuccessColor } from "../../DashStyleGuides";
import NoDataLabel from "../../NoDataLabel/index";
import buttonStyles from "../button.styles";
import { useStyles } from "./styles";
import { DateFilterTypes } from "../../../../util/enums";
import { translateMonthLabel } from "../../../../util/translateMonth";

/** Default Functional Component */
export default function OrderVolumeMetrics({ isTodaySelected }: { isTodaySelected: boolean }) {
  const classes = useStyles();
  const buttonClass = buttonStyles();
  const { t } = useTranslation();

  const { currentUser } = useAppState();

  const [selIndex, setSelectedIndex] = React.useState<number>(1);
  const [dataOnX, setDataOnX] = React.useState<string[]>([]);
  const [dataOnY, setDataOnY] = React.useState<(string | undefined)[]>([]);
  const [measurement, setMeasurement] = React.useState<string>("");

  useQuery(
    ["dashboard.order.performed", selIndex],
    () =>
      DashboardService.getOrderPerformed({
        companyId: currentUser?.companyId,
        dateFilterType: Object.values(DateFilterTypes)[selIndex],
      }),
    {
      onSuccess: (response) => {
        if (response && response?.data) {
          const xArrLabels: string[] = [];
          const yArrLabels: (string | undefined)[] = [];

          if (response?.data?.counts?.length === 0) {
            setDataOnX([]);
            setDataOnY([]);

            return; // means no data found
          }
          response?.data?.counts?.forEach((info) => {
            yArrLabels.push(info.total);
            switch (selIndex) {
              case 0:
                if (info?.hour !== undefined) xArrLabels.push(funcHourLabelLoader(info.hour));
                break;
              case 2:
                if (info?.year !== undefined) xArrLabels.push(info.year?.toString());
                break;
              default:
                if (info?.month && info?.year !== undefined)
                  xArrLabels.push(translateMonthLabel(`${info?.month} ${info.year}`, t));
                break;
            }
          });

          setDataOnX(xArrLabels);
          setDataOnY(yArrLabels);

          setMeasurement(response?.data?.measurement || "");
        }
      },
      enabled: !!currentUser?.companyId,
    }
  );

  useEffect(() => {
    if (isTodaySelected) {
      setSelectedIndex(isTodaySelected ? 0 : 1);
    }
  }, [isTodaySelected]);

  const funcHourLabelLoader = (hour: number) => {
    const convertedNum = hour.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
      useGrouping: false,
    });

    // eslint-disable-next-line no-nested-ternary
    return Number(convertedNum) < 9
      ? Number(convertedNum) === 0
        ? "12 AM"
        : `${convertedNum} AM`
      : `${convertedNum} PM`;
  };

  const renderMeasurement = () => {
    if (measurement) {
      return ` (${measurement?.toUpperCase()})`;
    }

    return null;
  };

  return (
    <>
      <Grid item xs={12}>
        <Paper className={classes.root} elevation={0}>
          <Toolbar className={classes.tblRoot} variant="dense">
            <Typography
              className={classes.title}
              color="textSecondary"
              data-cy="orderVolumeMetrics"
            >
              {t("orderVolume")}
              {renderMeasurement()}
            </Typography>
            <Box className={classes.bxFlexGrow} />
            <ButtonGroup color="info">
              {Object.keys(DateFilterTypes).map((filter, index) => (
                <Button
                  key={index}
                  className={classnames({
                    [buttonClass.buttonContained]: selIndex === index,
                    [buttonClass.buttonOutlined]: selIndex !== index,
                  })}
                  variant={selIndex === index ? "contained" : "outlined"}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedIndex(index);
                  }}
                >
                  {t(filter.toLowerCase())}
                </Button>
              ))}
            </ButtonGroup>
          </Toolbar>
          {dataOnY !== undefined && dataOnY.length === 0 ? (
            <NoDataLabel />
          ) : (
            <div className={classnames("chart-container", classes.orderWrapStyles)}>
              {dataOnX.length > 0 && dataOnY.length > 0 && (
                <Chart
                  id="barVolumeGraph"
                  chartType={ChartType.BAR}
                  graphOptions={{
                    maintainAspectRatio: false,
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: (tooltipItem: TooltipItem<"bar">) => {
                            if (tooltipItem?.raw) {
                              const rawValue = tooltipItem?.raw as string;

                              return rawValue?.toString();
                            }

                            return tooltipItem?.formattedValue;
                          },
                        },
                      },
                      legend: {
                        display: false,
                      },
                    },
                  }}
                  data={{
                    labels: dataOnX,

                    datasets: [
                      {
                        barThickness: 32,
                        data: dataOnY,
                        backgroundColor: brandSuccessColor,
                      },
                    ],
                  }}
                />
              )}
            </div>
          )}
        </Paper>
      </Grid>
    </>
  );
}
